import {
	useUserCompareDataQuery,
	useWorkplaceMemberQuery,
} from '../../graphql/graphql';
import { useAppContext } from '../../Services/AppContext';
import { useEffect, useMemo, useState } from 'react';
import {
	mapApiPatientDataToCompareTable,
	mapCsszPatientDataToCompareTable,
	mapNCZIDataToCompareTable,
	mapVzpPatientDataToCompareTable,
	PatientDataCompareKey,
} from '../../Helpers/Patient/mapPatientDataToCompareTable.helper';
import apiClient from '../../Services/ApiClient';
import useNotification from '../useNotification.hook';
import useNCZI from '../../Modules/Nczi/Hooks/useNCZI';
import { useNCZIContext } from '../../Modules/Nczi/Services/NCZIContext';
import useCertificates from '../../Modules/Certificates/Hooks/useCertificates';

export default function usePatientDataCompare(patientId: string) {
	const [validationData, setValidationData] =
		useState<Record<PatientDataCompareKey, string>>();
	const [NCZIData, setNCZIData] =
		useState<Record<PatientDataCompareKey, string>>();
	const { showNotificationAlert } = useNotification();
	const { patientSummaryContactData, patientIdentifier, patientBirthNumber } =
		useNCZIContext();
	const { fetchPatientSummaryContactData } = useNCZI();
	const [NCZICommandId, setNCZICommandId] = useState<string | null>(null);
	const [isValidationDataLoading, setIsValidationDataLoading] = useState(false);
	const { checkUserCertificateAvailability } = useCertificates();
	const {
		workplaceId,
		userData: { id: userId },
	} = useAppContext();

	const { data, isLoading, refetch } = useUserCompareDataQuery(
		{ id: patientId },
		{ refetchOnMountOrArgChange: true },
	);
	const { data: workplaceMemberData, isLoading: isWorkplaceMemberLoading } =
		useWorkplaceMemberQuery(
			{
				workplaceId,
				userId: patientId,
			},
			{ refetchOnMountOrArgChange: true },
		);

	useEffect(() => {
		if (!patientBirthNumber || !patientSummaryContactData) return;
		setNCZIData(
			mapNCZIDataToCompareTable(patientBirthNumber, patientSummaryContactData),
		);
	}, [patientSummaryContactData]);

	const handleFetchValidationData = async () => {
		setIsValidationDataLoading(true);
		try {
			const csszData = await handleFetchCSSZData();
			const csszMappedData = mapCsszPatientDataToCompareTable(csszData);

			const vzpData = await handleFetchVZPData();
			const vzpMappedData = mapVzpPatientDataToCompareTable(vzpData);

			setValidationData({
				...csszMappedData,
				...vzpMappedData,
				// vzp data are fetched by the original user insurance number, so it is still the same value
				insuranceNumber: original?.insuranceNumber || '',
			});
		} finally {
			setIsValidationDataLoading(false);
		}
	};

	const handleFetchCSSZData = async () => {
		try {
			const csszResult = await apiClient.get(
				`/workplaces/${workplaceId}/patient/${patientId}/cssz`,
				{ catchError: false },
			);

			return csszResult.data;
		} catch (error) {
			showNotificationAlert({
				content: 'component.csszResult.loadError',
			});

			return {};
		}
	};

	const handleFetchVZPData = async () => {
		try {
			const qualifiedCertificate = await checkUserCertificateAvailability(
				userId,
				'qualified',
			);

			if (qualifiedCertificate) {
				const data: any = await apiClient.post(
					`/patients/${patientId}/vzp`,
					[],
					{},
					false,
				);

				if (data?.data?.error) {
					showNotificationAlert({
						content: 'component.vzpResult.loadError',
					});
					return {};
				}

				if (data?.data?.extra) {
					return data?.data?.extra;
				}
			}

			return {};
		} catch (error) {
			showNotificationAlert({
				content: 'component.vzpResult.loadError',
			});

			return {};
		}
	};

	const handleFetchNCZIData = async () => {
		if (patientIdentifier) {
			const command = await fetchPatientSummaryContactData(
				patientIdentifier,
				patientId,
			);
			if (command.id) {
				setNCZICommandId(command.id);
			}
		}
	};

	const { virtual, original } = useMemo(
		() =>
			data
				? mapApiPatientDataToCompareTable(data)
				: { virtual: undefined, original: undefined },
		[data],
	);

	return {
		virtualData: virtual,
		originalData: original,
		validationData,
		NCZIData,
		NCZICommandId,
		workplaceMemberId: workplaceMemberData?.workplaceMember?.id,
		isLoading: isLoading || isWorkplaceMemberLoading,
		isValidationDataLoading,
		refetch,
		handleFetchNCZIData,
		handleFetchValidationData,
	};
}
